// NOTE: use-query-params v2 replaced the v1 `history`/`location` props on QueryParamProvider
// with an `adapter` render-prop component (see CHANGELOG "Migrating from v1"). This is a custom
// adapter for the Next.js pages router, derived from the original shim:
// https://github.com/pbeshai/use-query-params/issues/13#issuecomment-607785336
//
// We pass query-string's parse/stringify via `options` to retain v1 serialization behavior.
// v2 otherwise defaults to URLSearchParams, which changes URL encoding (spaces as `+`) and key
// ordering (insertion vs. alphabetical) — query-string keeps URLs and link generation identical.

import React, { memo, useMemo } from 'react';
import { useRouter } from 'next/compat/router';
import { QueryParamProvider as ContextProvider } from 'use-query-params';
import { parse, stringify } from 'query-string';

const queryStringOptions = {
  searchStringToObject: parse,
  objectToSearchString: stringify,
};

// Bridges the Next.js router to use-query-params via the v2 QueryParamAdapter contract
// ({ location, push, replace }) and hands it to the library through the render-prop child.
const NextRouterAdapter = ({ children }) => {
  const router = useRouter();
  // next/compat/router can return null (router not ready); default asPath so the adapter never
  // assumes a fully-populated router object.
  const asPath = router?.asPath ?? '';
  const match = asPath.match(/[^?]+/);
  const pathname = match ? match[0] : asPath;

  const adapter = useMemo(
    () => ({
      location: {
        search: asPath.replace(/[^?]+/u, ''),
      },
      push: ({ search }) => {
        router?.push(`${router?.pathname}${search}`, `${pathname}${search}`, { shallow: true });
      },
      replace: ({ search }) => {
        router?.replace(`${router?.pathname}${search}`, `${pathname}${search}`, { shallow: true });
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, router?.pathname, asPath]
  );

  return children(adapter);
};

const QueryParamProvider = ({ children }) => (
  <ContextProvider adapter={NextRouterAdapter} options={queryStringOptions}>
    {children}
  </ContextProvider>
);

const MemoizedQueryParamProvider = memo(QueryParamProvider);

export default MemoizedQueryParamProvider;
